import { useEffect, useRef } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  MinusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface PresentationToolbarProps {
  currentSlide: number;
  totalSlides: number;
  productName: string;
  filterSummary?: string;
  zoomLevel: number;
  visible: boolean;
  onNext: () => void;
  onPrev: () => void;
  onGoTo: (index: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onExit: () => void;
}

export function PresentationToolbar({
  currentSlide,
  totalSlides,
  productName,
  filterSummary,
  zoomLevel,
  visible,
  onNext,
  onPrev,
  onGoTo,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onExit,
}: PresentationToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Prevent keyboard events from propagating when clicking toolbar buttons
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('keydown', stop);
    return () => el.removeEventListener('keydown', stop);
  }, []);

  return (
    <div
      ref={toolbarRef}
      className={`fixed top-0 left-0 right-0 z-[10002] transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="mx-auto max-w-4xl mt-4 px-4">
        <div className="bg-gray-900/90 backdrop-blur-sm rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4">
          {/* Navigation arrows */}
          <button
            onClick={onPrev}
            disabled={currentSlide === 0}
            className="p-1.5 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Previous (←)"
          >
            <ChevronLeftIcon className="w-5 h-5 text-white" />
          </button>

          {/* Product name & slide indicator */}
          <div className="flex-1 text-center min-w-0">
            <h2 className="text-sm font-semibold text-white truncate">
              {productName}
            </h2>
            {filterSummary && (
              <p className="text-[10px] text-gray-400 truncate mt-0.5">{filterSummary}</p>
            )}
          </div>

          <button
            onClick={onNext}
            disabled={currentSlide === totalSlides - 1}
            className="p-1.5 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Next (→)"
          >
            <ChevronRightIcon className="w-5 h-5 text-white" />
          </button>

          {/* Divider */}
          <div className="w-px h-6 bg-white/20" />

          {/* Zoom controls */}
          <div className="flex items-center gap-1">
            <button
              onClick={onZoomOut}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              title="Zoom out (-)"
            >
              <MinusIcon className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={onZoomReset}
              className="px-2 py-1 rounded-lg hover:bg-white/10 transition-colors text-xs text-white font-medium min-w-[3rem] text-center"
              title="Reset zoom (0)"
            >
              {Math.round(zoomLevel * 100)}%
            </button>
            <button
              onClick={onZoomIn}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              title="Zoom in (+)"
            >
              <PlusIcon className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-white/20" />

          {/* Slide counter */}
          <span className="text-xs text-gray-300 whitespace-nowrap">
            {currentSlide + 1} / {totalSlides}
          </span>

          {/* Exit button */}
          <button
            onClick={onExit}
            className="p-1.5 rounded-lg hover:bg-red-500/20 transition-colors"
            title="Exit presentation (Esc)"
          >
            <XMarkIcon className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Slide dots */}
      {totalSlides <= 12 && (
        <div className="flex justify-center mt-2 gap-1.5">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => onGoTo(i)}
              className={`rounded-full transition-all ${
                i === currentSlide
                  ? 'w-6 h-2 bg-white'
                  : 'w-2 h-2 bg-white/40 hover:bg-white/70'
              }`}
              title={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
